import Game from '@/models/Game';
import MatchSide from '@/models/MatchSide';
import { CompetitionFormat, TeamScoringUnit, WinCondition } from '@/services/utils/enums';
import { translate } from '@/i18n';

import { makeLogger } from '@/services/utils/logger';
import { LOGGING } from '@/config';
const DEBUG_LOG = false;
const log = makeLogger('Series', DEBUG_LOG, LOGGING.MODELS_LOG);

function getSeriesPlayerSideKey(series, playerId) {
  if (!playerId) return null;
  if (seriesSideContainsPlayer(series?.side1, playerId)) return 'side1';
  if (seriesSideContainsPlayer(series?.side2, playerId)) return 'side2';
  return null;
}

function getSeriesParticipantSideKey(series, participantId) {
  if (!participantId) return null;
  if (series?.side1?.id === participantId || series?.side1?.meta?.participantId === participantId) return 'side1';
  if (series?.side2?.id === participantId || series?.side2?.meta?.participantId === participantId) return 'side2';
  return null;
}

function seriesSideContainsPlayer(side, playerId) {
  if (!side || !playerId) return false;
  if (side.id === playerId) return true;
  if (side.entity?.id === playerId) return true;
  if (Array.isArray(side.meta?.entityIds) && side.meta.entityIds.includes(playerId)) return true;
  return Array.isArray(side.members) && side.members.some((member) => member?.id === playerId);
}

/**
 * Универсальная модель серии.
 *
 * Series — это верхний уровень агрегирования для стадии плей-офф:
 * - в личном турнире `items` содержит `Game[]`
 * - в командном турнире `items` содержит `TeamMatch[]`
 *
 * Таким образом цепочка данных выглядит так:
 *   Game -> TeamMatch -> Series
 *
 * где:
 * - Game       — отдельная партия;
 * - TeamMatch  — один матч между двумя командами, собранный из нескольких Game;
 * - Series     — серия из нескольких Game (личный случай) или TeamMatch (командный случай).
 */
export default class Series {
  /**
   * @param {Object} params
   * @param {'individual'|'team'} [params.seriesType='individual'] - Тип серии.
   * @param {Object} params.side1 - Первая сторона серии.
   * @param {Object} params.side2 - Вторая сторона серии.
   * @param {Array} [params.items=[]] - Элементы серии: Game[] или TeamMatch[].
   * @param {number} [params.countToWin] - Порог победы в серии.
   * @param {'games'|'score'} [params.winCondition] - Условие победы.
   * @param {'points'|'sets'|null} [params.teamScoringUnit=null] - Единица подсчёта командной серии.
   * @param {string|null} [params.stage] - Стадия плей-офф.
   * @param {number|null} [params.stageNumber] - Порядковый номер стадии.
   * @param {number|null} [params.bracketIndex] - Позиция в сетке.
   * @param {string|null} [params.label=null] - Подпись серии для UI.
   * @param {Object} [params.meta={}] - Служебные данные.
   */
  constructor({
    seriesType = CompetitionFormat.INDIVIDUAL,
    side1,
    side2,
    items = [],
    countToWin,
    winCondition,
    teamScoringUnit = null,
    stage,
    stageNumber,
    bracketIndex,
    label = null,
    meta = {},
  }) {
    this.seriesType = seriesType;
    this.side1 = Series.normalizeSide(side1);
    this.side2 = Series.normalizeSide(side2);
    this.items = Array.isArray(items) ? items : [];
    this.countToWin = countToWin;
    this.winCondition = winCondition;
    this.teamScoringUnit = teamScoringUnit;
    this.stage = stage;
    this.stageNumber = stageNumber;
    this.label = label ?? (stage
      ? translate(`playoffStages.${stage}`)
      : translate('eliminationPairings.playoff'));
    this.bracketIndex = bracketIndex;
    this.meta = meta ?? {};
  }

  static normalizeSide(side) {
    return MatchSide.fromObject(side);
  }

  /**
   * Возвращает `true`, если серия личная и состоит из Game[].
   * @returns {boolean}
   */
  isPlayerSeries() {
    return this.seriesType === CompetitionFormat.INDIVIDUAL;
  }

  /**
   * Возвращает `true`, если серия командная и состоит из TeamMatch[].
   * @returns {boolean}
   */
  isTeamSeries() {
    return this.seriesType === CompetitionFormat.TEAM;
  }

  /**
   * Возвращает базовую сущность стороны для UI.
   * Для player-series это Player, для team-series — Team или null для временной команды.
   * @param {'side1'|'side2'} sideKey
   * @returns {Player|import('@/models/Team').default|Object|null}
   */
  getSide(sideKey) {
    const side = sideKey === 'side2' ? this.side2 : this.side1;
    return side?.entity ?? null;
  }

  /**
   * Считает текущий счёт серии.
   *
   * Личный режим:
   * - считаем очки по Game.
   *
   * Командный режим:
   * - при `teamScoringUnit = points` суммируем очки TeamMatch;
   * - при `teamScoringUnit = sets` считаем победы по TeamMatch.
   *
   * @returns {Record<string, number>}
   */
  getScore() {
    log('getScore', '📊 Серия:', this);
    const scores = {
      [this.side1.id]: 0,
      [this.side2.id]: 0,
    };

    if (this.isTeamSeries()) {
      for (const item of this.items) {
        if (!item?.result) continue;

        const side1Score = Number(item.score?.side1 ?? item.result?.side1Score ?? 0);
        const side2Score = Number(item.score?.side2 ?? item.result?.side2Score ?? 0);
        const itemSide1Id = item.side1?.id ?? null;
        const itemSide2Id = item.side2?.id ?? null;

        if (this.teamScoringUnit === TeamScoringUnit.POINTS) {
          if (Object.prototype.hasOwnProperty.call(scores, itemSide1Id)) scores[itemSide1Id] += side1Score;
          if (Object.prototype.hasOwnProperty.call(scores, itemSide2Id)) scores[itemSide2Id] += side2Score;
          continue;
        }

        if (item.result === '1-0' || item.result?.winnerSide === 'side1') {
          if (Object.prototype.hasOwnProperty.call(scores, itemSide1Id)) scores[itemSide1Id] += 1;
        }
        else if (item.result === '0-1' || item.result?.winnerSide === 'side2') {
          if (Object.prototype.hasOwnProperty.call(scores, itemSide2Id)) scores[itemSide2Id] += 1;
        }
        else if (item.result === '½-½' || item.result?.isDraw) {
          if (Object.prototype.hasOwnProperty.call(scores, itemSide1Id)) scores[itemSide1Id] += 0.5;
          if (Object.prototype.hasOwnProperty.call(scores, itemSide2Id)) scores[itemSide2Id] += 0.5;
        }
      }

      return scores;
    }

    for (const game of this.items) {
      const whitePlayerId = game?.getSingleWhitePlayerId?.();
      const blackPlayerId = game?.getSingleBlackPlayerId?.();
      const whiteSideKey = getSeriesParticipantSideKey(this, game?.getSideParticipantId?.('white'))
        ?? getSeriesPlayerSideKey(this, whitePlayerId);
      const blackSideKey = getSeriesParticipantSideKey(this, game?.getSideParticipantId?.('black'))
        ?? getSeriesPlayerSideKey(this, blackPlayerId);
      if (game.result === '1-0' && whiteSideKey) scores[this[whiteSideKey].id] += 1;
      else if (game.result === '0-1' && blackSideKey) scores[this[blackSideKey].id] += 1;
      else if (game.result === '½-½') {
        if (whiteSideKey) scores[this[whiteSideKey].id] += 0.5;
        if (blackSideKey) scores[this[blackSideKey].id] += 0.5;
      }
    }

    return scores;
  }

  /**
   * Определяет победителя серии в соответствии с winCondition/countToWin.
   * Возвращает базовую сущность стороны (`entity`) или `null`, если победитель ещё не определён.
   * @returns {Player|import('@/models/Team').default|Object|null}
   */
  getWinner() {
    if (this.meta.automaticAdvance) return this.side1.entity ?? this.side1;
    const score = this.getScore();
    const score1 = score[this.side1.id];
    const score2 = score[this.side2.id];
    log('getWinner', '🏁 Проверка победителя для серии:', {
      winCondition: this.winCondition,
      countToWin: this.countToWin,
      score1,
      score2,
      itemsPlayed: this.items.length
    });

    if (this.winCondition === WinCondition.GAMES) {
      const total = this.items.length;
      if (total >= this.countToWin && score1 !== score2) {
        return score1 > score2 ? this.side1.entity : this.side2.entity;
      }
      return null;
    }

    if (this.winCondition === WinCondition.SCORE) {
      if (score1 >= this.countToWin && score1 > score2) return this.side1.entity;
      if (score2 >= this.countToWin && score2 > score1) return this.side2.entity;
    }

    return null;
  }

  /**
   * Возвращает историю элементов серии в удобном для UI виде.
   *
   * - для player-series история строится по Game;
   * - для team-series история строится по TeamMatch.
   *
   * @returns {Array<Object>}
   */
  getHistory() {
    if (this.isTeamSeries()) {
      return this.items.map((item) => ({
        itemId: item?.teamMatchKey ?? item?.id ?? null,
        side1Score: item?.score?.side1 ?? item?.result?.side1Score ?? 0,
        side2Score: item?.score?.side2 ?? item?.result?.side2Score ?? 0,
        label: item?.score?.summary ?? item?.result?.summary ?? '',
      }));
    }

    return this.items.map((game) => {
      const { id, result } = game;
      const whitePlayerId = game?.getSingleWhitePlayerId?.();
      const blackPlayerId = game?.getSingleBlackPlayerId?.();
      const whiteSideKey = getSeriesParticipantSideKey(this, game?.getSideParticipantId?.('white'))
        ?? getSeriesPlayerSideKey(this, whitePlayerId);
      const blackSideKey = getSeriesParticipantSideKey(this, game?.getSideParticipantId?.('black'))
        ?? getSeriesPlayerSideKey(this, blackPlayerId);
      const side1IsWhite = whiteSideKey === 'side1';
      const side2IsWhite = whiteSideKey === 'side2';

      let side1Score = 0;
      let side2Score = 0;

      if (result === '1-0') {
        if (whiteSideKey === 'side1') side1Score = 1;
        else if (whiteSideKey === 'side2') side2Score = 1;
      } else if (result === '0-1') {
        if (blackSideKey === 'side1') side1Score = 1;
        else if (blackSideKey === 'side2') side2Score = 1;
      } else if (result === '½-½') {
        side1Score = 0.5;
        side2Score = 0.5;
      }

      return {
        gameID: id,
        side1Score,
        side2Score,
        side1Color: side1IsWhite ? 'white' : 'black',
        side2Color: side2IsWhite ? 'white' : 'black',
        label: `${side1Score}${side1IsWhite ? 'w' : 'b'}-${side2Score}${side2IsWhite ? 'w' : 'b'}`,
      };
    });
  }

  /**
   * Возвращает ID противоположной стороны серии.
   * @param {string} sideId
   * @returns {string|null}
   */
  getSideOpponentId(sideId) {
    if (sideId === this.side1.id) return this.side2.id;
    if (sideId === this.side2.id) return this.side1.id;
    return null;
  }

  /**
   * Возвращает только те items, которые являются Game.
   * Нужен для мест, где UI ещё работает в player-centric режиме.
   * @returns {Game[]}
   */
  getGameItems() {
    return this.isPlayerSeries() ? this.items.filter((item) => item instanceof Game) : [];
  }
}
